import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { transparentize } from '@chakra-ui/theme-tools';
import { format } from 'date-fns';
import { MouseEvent, useState } from 'react';
import { Row } from '@tanstack/react-table';
import { ListItemText } from 'app/components/ChakraTableList';
import { GenericObject } from 'types/generic.types';
import { LabelV2Version } from 'types/releases';
import { dateFormats } from 'app/constants';
import { AudioPlayControls } from 'app/components/Song/Audio/AudioPlayControls';
import { AudioTitle } from 'app/components/Song/Audio/AudioTitle';
import { useAudioPlayHook } from 'app/components/Song/Audio/AudioPlayControls/AudioPlayHook';
import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';
import { showRowTopShadow } from 'app/components/DraggableListRow/utils';
import { DebouncedFuncLeading } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Center } from '@chakra-ui/react';

interface ReleaseVersionTableRowProps {
  index: number;
  row: Row<LabelV2Version>;
  currentlyReplacingAudio: GenericObject<boolean>;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
  onRowReorder?: (
    sourceIndex: number,
    targetIndex: number,
    isTop: boolean
  ) => void;
  onDragging: DebouncedFuncLeading<(rowIndex: number) => void>;
  href: string;
}

export const ReleaseVersionTableRow = ({
  index: rowIndex,
  row,
  currentlyReplacingAudio,
  contextHandler,
  onRowReorder,
  onDragging,
  href,
}: ReleaseVersionTableRowProps) => {
  const navigate = useNavigate();
  const version = row.original;

  const {
    song,
    updated,
    primaryAudioGuid,
    originalAudioGuid,
    title,
    guid,
    isNew,
  } = version;

  const parentGuid = isNew ? song.guid : guid;
  const audioGuid = primaryAudioGuid ?? originalAudioGuid;

  const audioPlay = useAudioPlayHook({
    audioGuid,
    parentGuid,
    songDetails: { title: song.title },
    versionSongGuid: song.guid,
  });

  const isLoading = ({ isAudioLoading, audioPlayerGuid }) =>
    (isAudioLoading && audioGuid === audioPlayerGuid) ||
    currentlyReplacingAudio[guid];

  const [isTop, setIsTop] = useState(false);
  let divRef: HTMLDivElement | null;
  const [{ isDragging }, drag] = useDrag({
    type: 'ROW_DRAG',
    item: { index: rowIndex },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'ROW_DRAG',
    drop({ index }: { index: number }) {
      onRowReorder?.(index, rowIndex, isTop);
    },
    hover: (
      item: {
        index: number;
      },
      monitor: DropTargetMonitor
    ) => {
      if (onDragging) {
        onDragging(item.index);
      }
      setIsTop(showRowTopShadow(divRef, monitor));
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Tr
      ref={(ref) => {
        if (ref) {
          divRef = ref;
        }
        drag(drop(ref));
      }}
      zIndex={1}
      position="relative"
      role="row"
      data-group
      clickable
      onContextMenu={contextHandler}
      onClick={() => navigate(href)}
      sx={{
        ...(isDragging
          ? {
              bg: transparentize('M.10', 0.36),
            }
          : {}),
        ...(isOver
          ? {
              bg: 'transparent !important',
              cursor: 'grabbing !important',
            }
          : {}),
        ...(isOver
          ? {
              boxShadow: isTop
                ? `inset 0px 1px 0px 0px var(--chakra-colors-B-80)`
                : `0px 1px 0px 0px var(--chakra-colors-B-80)`,
            }
          : {}),
      }}
    >
      <Td position="relative">
        {audioGuid ? (
          <>
            <AudioPlayControls
              isLoading={isLoading}
              audioGuid={audioGuid}
              parentGuid={parentGuid}
              songDetails={{ title: song.title }}
              versionSongGuid={song.guid}
              sx={{
                zIndex: 1,
                visibility: audioPlay.isActive ? 'visible' : 'hidden',
                _groupHover: {
                  visibility: 'visible',
                },
              }}
            />
            {audioPlay.isActive ? (
              ''
            ) : (
              <Center position="absolute" top={0} lineHeight="48px" minW={5}>
                {rowIndex + 1}
              </Center>
            )}
          </>
        ) : (
          <Center>{rowIndex}</Center>
        )}
      </Td>
      <Td sx={{ pl: 0 }}>
        <AudioTitle
          audioGuid={primaryAudioGuid}
          title={title || ''}
          isLatestVersion={false}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="title"
          primary={song.title}
          title={song.title}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="date"
          primary={format(new Date(updated.iso), dateFormats.LIST_FORMAT)}
        />
      </Td>
      <Td />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};
